import {useEffect, useRef, useState} from "react";
import {useStore} from "../contexts/AppContext";
import {useLocation} from "react-router-dom";
import {Link2, RefreshCcw} from 'lucide-react';
import {useAuth} from "../contexts/AuthContext";
import * as analytics from "../utils/Analytics";
import {initStructure} from "../utils/initStructure";
import {useFetchData} from "../hooks/useFetchData";
import {Badge, Button, Card, Input, Table, Tag, theme,} from 'antd';
import api from "../http";
import MainTableModal from "./MainTableModal";
import {titleHead} from "../utils/constants";
import {PushpinOutlined, SearchOutlined} from "@ant-design/icons";


const MainTable = () => {
    const fetchData = useFetchData();
    const url = useLocation();
    const clearUrl = url.pathname.replace('/dashboard/', '')
    const department_id = Number(localStorage.getItem("department_id"));
    const searchInput = useRef(null);
    const [tableFilters, setTableFilters] = useState({});
    const [pageSize, setPageSize] = useState(10);
    const [sortState, setSortState] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchLoader, setSearchLoader] = useState(false)
    const {
        setMode, setTab, dataLoading, setDataLoading, documents, setDocuments, reloadButton, setReloadButton,
        draftNewDocument, setDraftNewDocument, setRow, activePage, setActivePage, docCount, setDocCount,
        open, setOpen, setNeedAccess,
    } = useStore()
    const {userInfo} = useAuth()
    const {token} = theme.useToken();


    useEffect(() => {

        const stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
        const storedPageSize = localStorage.getItem('pageSize');
        if (storedPageSize) {
            setPageSize(Number(storedPageSize));
        }
        setSortState(stored[clearUrl]?.sort || null);
        setSearchText(stored[clearUrl]?.search || '');
        if (stored[clearUrl]) {
            setTableFilters(stored[clearUrl]);
        } else {
            setTableFilters({});
        }
        loadPage(storedPageSize ? Number(storedPageSize) : pageSize, 1)
        // eslint-disable-next-line
    }, [clearUrl]);


    useEffect(() => {
        const abortController = new AbortController();
        setTab('info')
        fetchData(abortController.signal);

        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line
    }, []);


    const loadPage = async (quantity, active_page) => {
        setDataLoading(true)
        const stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
        const params = new URLSearchParams();

        Object.entries(stored[clearUrl] || {}).forEach(([key, value]) => {
            if (value) {
                if (key === 'search') {
                    params.append(key, value);
                } else {
                    params.append(key, JSON.stringify(value));
                }
            }
        });
        try {
            const docResponse = await api.get(`supabase/my-documents/${clearUrl}/${quantity}/${active_page}?${params}`);
            if (docResponse.status === 200 && docResponse.data.data) {
                setDocuments(docResponse.data.data);
                setDocCount(docResponse.data.count);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    };


    const handleSearch = async (selectedKeys, close, dataIndex) => {
        let stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
        if (!stored[clearUrl]) stored[clearUrl] = {};

        if (selectedKeys.length === 0) {
            delete stored[clearUrl][dataIndex];
            if (Object.keys(stored[clearUrl]).length === 0) {
                delete stored[clearUrl];
            }
        } else {
            stored[clearUrl][dataIndex] = selectedKeys;
        }

        localStorage.setItem('tableFilters', JSON.stringify(stored));
        setActivePage(1);
        await loadPage(pageSize, 1);
        setTableFilters(stored[clearUrl] || {});
        close()
    };

    const handleReset = async (clearFilters, close, dataIndex) => {
        const newFilters = {...tableFilters};
        delete newFilters[dataIndex];
        setTableFilters(newFilters);
        let stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
        stored[clearUrl] = newFilters;
        localStorage.setItem('tableFilters', JSON.stringify(stored));
        setActivePage(1);
        await loadPage(pageSize, 1);
        clearFilters();
        close()
    };


    const getColumnSearchProps = dataIndex => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, clearFilters, close}) => (
            <div style={{padding: 8}} onKeyDown={e => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, close, dataIndex)}
                    style={{marginBottom: 8, display: 'block'}}
                />
                <div style={{display: "flex", gap: 5}}>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, close, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, close, dataIndex)}
                        size="small"
                        style={{width: 90}}
                    >
                        Reset
                    </Button>
                    <Button
                        size="small"
                        onClick={close}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: text => {
            return text
        },
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        sortOrder: sortState ? sortState : null,
    });

    const columns = [
        {
            title: '№',
            render: (_, rowData, rowIndex) => {
                return <span>{rowIndex + 1}</span>
            },
            width: 50
        },
        {
            key: 'title',
            dataIndex: "title",
            title: 'Title',
            ...getColumnSearchProps('title'),
            filteredValue: tableFilters.title || null,
            sortOrder: sortState?.field === 'title' ? sortState?.order : null,

        },
        {
            key: 'document_category_title',
            dataIndex: "document_category_title",
            title: 'Category',
            ...getColumnSearchProps('document_category_title'),
            filteredValue: tableFilters.document_category_title || null,
            sortOrder: sortState?.field === 'document_category_title' ? sortState?.order : null,

        },
        {
            key: 'document_type_title',
            dataIndex: "document_type_title",
            title: 'Type',
            ...getColumnSearchProps('document_type_title'),
            filteredValue: tableFilters.document_type_title || null,
            sortOrder: sortState?.field === 'document_type_title' ? sortState?.order : null,
        },
        {
            key: 'department_title',
            dataIndex: "department_title",
            title: 'Department',
            ...getColumnSearchProps('department_title'),
            filteredValue: tableFilters.department_title || null,
            sortOrder: sortState?.field === 'department_title' ? sortState?.order : null,
        },
        {
            key: 'brand_title',
            dataIndex: "brand_title",
            title: 'Brand',
            ...getColumnSearchProps('brand_title'),
            filteredValue: tableFilters.brand_title || null,
            sortOrder: sortState?.field === 'brand_title' ? sortState?.order : null,
        },
        {
            title: 'Status',
            key: 'is_public',
            dataIndex: "is_public",
            render: (_, {is_public}) => (
                <>
                    {is_public === true &&
                        <Badge status="warning" text="Public"/>
                    }
                    {is_public === false &&
                        <Badge status="success" text="Secured"/>
                    }
                    {is_public === null &&
                        <Badge status="processing" text="Good"/>
                    }
                </>
            ),
            filters: [
                {
                    text: 'Public',
                    value: true,
                },
                {
                    text: 'Secured',
                    value: false,
                },
                {
                    text: 'Good',
                    value: null,
                },
            ],
            filteredValue: tableFilters.is_public || [],
        },
        {
            title: 'Tags',
            key: 'tags_array',
            render: (_, {tags_array}) => (
                <>
                    {tags_array.map((tag) => {
                        let color = tag.length > 5 ? 'geekblue' : 'green';
                        if (tag === 'loser') {
                            color = 'volcano';
                        }
                        return (
                            <Tag color={color} key={tag}>
                                {tag.toUpperCase()}
                            </Tag>
                        );
                    })}
                </>
            ),
            filteredValue: null,
        },
        {
            key: 'document_url',
            dataIndex: 'document_url',
            title: 'Url',
            width: 60,
            render: (_, {document_url}) => {
                if (!document_url) {
                    return null;
                }
                return (
                    <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%"}}>
                        <Button
                            size="xs"
                            icon={<Link2 size={16}/>}
                            href={document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                );
            },
            filteredValue: null,
        },
        {
            key: 'owner',
            dataIndex: 'owner',
            title: 'Owner',
            render: (_, rowData) => {
                const ownerUser = rowData.current_roles_json.find(user => user.role_id === 1)
                return <span>{ownerUser ? ownerUser?.email : ""}</span>
            },
            filteredValue: null,
        },
        {
            key: 'responsible',
            dataIndex: 'responsible',
            title: 'Responsible',
            render: (_, rowData) => {
                const respUser = rowData.current_roles_json.filter(user => user.role_id === 2)
                return <span>{respUser.length ? respUser[0]?.email : rowData.current_roles_json.find(user => user.role_id === 1)?.email}</span>
            },
            filteredValue: null,
        },
        {
            key: 'my_role',
            dataIndex: 'my_role',
            title: 'My role',
            render: (_, rowData) => {
                return <span>{rowData.role_title || ""}</span>
            },
            filteredValue: null,
        },
        {
            key: 'date_start',
            dataIndex: 'date_start',
            title: 'Date start',
            render: (_, rowData) => {
                return <span>{rowData.created_at ? new Date(rowData.date_start).toLocaleDateString('ru-RU') : null}</span>
            },
            filteredValue: null,
        },

        {
            key: 'date_end',
            dataIndex: 'date_end',
            title: 'Date end',
            render: (_, rowData) => {
                return <span>{rowData.created_at ? new Date(rowData.date_end).toLocaleDateString('ru-RU') : null}</span>
            },
            filteredValue: null,
        },
        {
            key: 'created_at',
            dataIndex: 'created_at',
            title: 'Created',
            render: (_, rowData) => {
                return <span>{rowData.created_at ? new Date(rowData.created_at).toLocaleString('ru-RU') : null}</span>
            },
            filteredValue: null,
        },
        {
            key: 'updated_at',
            dataIndex: 'updated_at',
            title: 'Updated',
            render: (_, rowData) => {
                return <span>{rowData.updated_at ? new Date(rowData.updated_at).toLocaleString('ru-RU') : null}</span>
            },
            filteredValue: null,
        },
    ];

    const handleClose = () => {
        setOpen(false);
        setMode("")
        setTab("info")
    }

    const onRowClick = (rowData) => {
        setMode("editDocument")
        setTab('info')
        setNeedAccess([])
        setRow(rowData);
        setOpen(true);

        const key = `doc_opened_${rowData.id}`;
        if (!sessionStorage.getItem(key)) {
            analytics.sendEvent('doc_opened', {doc_id: rowData?.id || null, doc_url: rowData?.document_url || null});
            sessionStorage.setItem(key, '1');
        }
    }

    const onAddNewDocument = () => {
        localStorage.removeItem("draftNewDocument")
        setDraftNewDocument(false)
        setMode("newDocument");
        setRow(initStructure)
        setRow(prevState => ({
            ...prevState,
            department_id: department_id ? department_id : null,
            current_roles_json: [
                {
                    profile_id: userInfo.profile_id,
                    email: userInfo.email,
                    role_id: 1
                }
            ]
        }))
        setOpen(true);
    }

    const onOpenDraftDocument = () => {
        const draftDocument = JSON.parse(localStorage.getItem("draftNewDocument"))
        if (draftDocument) {
            setMode("newDocument");
            setRow(draftDocument)
            setOpen(true);
        }
    }

    const onChangePagination = async (pagination, filters, sorter) => {
        let stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
        if (!stored[clearUrl]) stored[clearUrl] = {};

        if (filters.is_public?.length) {
            stored[clearUrl]['is_public'] = filters.is_public;
        } else if (!filters.is_public?.length) {
            delete stored[clearUrl]["is_public"]
        }

        if (sorter?.order) {
            stored[clearUrl].sort = {
                field: sorter.field,
                order: sorter.order,
            };
        } else {
            stored[clearUrl].sort = null;
        }
        setTableFilters(stored[clearUrl] || {});
        setSortState(stored[clearUrl].sort);
        localStorage.setItem('tableFilters', JSON.stringify(stored));
        setPageSize(pagination.pageSize)
        localStorage.setItem('pageSize', pagination.pageSize.toString());
        await loadPage(pagination.pageSize, pagination.current);
        setActivePage(pagination.current);
    }


    return (
        <div
            style={{
                width: "100vw",
                minHeight: "90vh",
                background: token.colorBgContainer,
                margin: 30,
                overflowX: "hidden",
                borderRadius: 10,
            }}
        >
            <Card
                title={titleHead[clearUrl]}
                variant={'borderless'}
                extra={
                    <div style={{display: "flex", gap: 10, alignItems: "center"}}>
                        <Button type="primary" onClick={onAddNewDocument} hidden={clearUrl === "archive"}>
                            New Document
                        </Button>
                        {draftNewDocument &&
                            <Button
                                onClick={onOpenDraftDocument}
                                icon={<PushpinOutlined />}
                            />
                        }
                        <Input.Search
                            placeholder="Search documents..."
                            allowClear
                            enterButton
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={async (value) => {
                                let stored = JSON.parse(localStorage.getItem('tableFilters') || '{}');
                                if (!stored[clearUrl]) stored[clearUrl] = {};

                                if (value && value.trim()) {
                                    stored[clearUrl].search = value.trim();
                                } else {
                                    delete stored[clearUrl].search;
                                }
                                
                                localStorage.setItem('tableFilters', JSON.stringify(stored));
                                setTableFilters(stored[clearUrl] || {});

                                setActivePage(1);
                                setSearchLoader(true);
                                try {
                                    await loadPage(pageSize, 1);
                                } catch (error) {
                                    console.error('Error during search:', error);
                                    throw error;
                                } finally {
                                    setSearchLoader(false);
                                }
                            }}
                            style={{ width: 300 }}
                            loading={searchLoader}
                        />
                        
                        <Button
                            loading={reloadButton}
                            onClick={async () => {
                                setReloadButton(true);
                                await loadPage(pageSize, activePage);
                                setReloadButton(false);
                            }}
                            icon={<RefreshCcw size={15}/>}
                        />
                    </div>
                }
                style={{
                    width: "100%",
                    height: "100%",
                    overflowX: "auto",
                    background: token.colorBgContainer,
                }}
            >
                <>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={documents}
                    loading={dataLoading}
                    size={"small"}
                    scroll={{x: "max-content", y: "calc(100vh - 320px)"}}
                    onRow={(rowData) => ({
                        onClick: () => onRowClick(rowData),
                    })}
                    pagination={{
                        current: activePage,
                        pageSize: pageSize,
                        total: docCount,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} элементов`,
                    }}
                    onChange={onChangePagination}
                />
                </>

                <MainTableModal open={open} handleClose={handleClose}/>
            </Card>
        </div>
    )
}

export default MainTable